import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * Card (DESIGN_SYSTEM §4): raised surface, 1 px line border, radius xl, resting shadow; 20 px
 * padding. `variant="raised"` is the main card that overlaps the blue band (`--shadow-raised`).
 */
function Card({
  className,
  variant = 'default',
  ...props
}: React.ComponentProps<'section'> & { variant?: 'default' | 'raised' }) {
  return (
    <section
      data-slot="card"
      data-variant={variant}
      className={cn(
        'flex flex-col rounded-xl border bg-surface text-foreground',
        variant === 'raised' ? 'shadow-raised' : 'shadow-card',
        className,
      )}
      {...props}
    />
  );
}

/** Title (16/24 600, display face) left, optional actions right — e.g. the card's "Edit" / "Record change" button. */
function CardHeader({
  className,
  title,
  description,
  actions,
  headingLevel = 2,
  children,
  ...props
}: Omit<React.ComponentProps<'div'>, 'title'> & {
  title?: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  headingLevel?: 2 | 3;
}) {
  const Heading = headingLevel === 2 ? 'h2' : 'h3';
  return (
    <div
      data-slot="card-header"
      className={cn('flex flex-wrap items-start justify-between gap-3 px-5 pt-5', className)}
      {...props}
    >
      <div className="min-w-0">
        {title ? <Heading className="text-section text-foreground">{title}</Heading> : null}
        {description ? (
          <p className="mt-0.5 text-small text-muted-foreground">{description}</p>
        ) : null}
        {children}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  );
}

function CardContent({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="card-content" className={cn('p-5', className)} {...props} />;
}

function CardFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-footer"
      className={cn('flex items-center justify-end gap-2 border-t px-5 py-4', className)}
      {...props}
    />
  );
}

export { Card, CardContent, CardFooter, CardHeader };
